import { UseCreationStatusResult } from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-creation-status";
import { useTranslation } from "@redotech/redo-i18n";
import FaceFrownIcon from "@redotech/redo-icons/face-frown.svg";
import { Status } from "@redotech/redo-model/returns/temporal-status";
import { ReturnTypeEnum } from "@redotech/redo-return-core-common/return-type-enum";
import { RedoProgressBar } from "@redotech/redo-web/arbiter-components/progress-bar/redo-progress-bar";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { memo } from "react";
import { TypeAnimation } from "react-type-animation";
import { Card } from "../../submission/components/card";
import * as confirmationCss from "../confirmation.module.css";

const EXPECTED_WORKFLOW_DURATION_MS = 30_000; // Increased since we're tracking the full workflow chain
const MAX_TIME_BASED_PROGRESS = 80; // Max progress from time alone (80%)
const ACTIVITY_BOOST = 1.5; // 1.5% per completed activity (reduced since we have fewer visible activities)
const ESTIMATED_TOTAL_ACTIVITIES = 25; // Reduced since we're only showing creation workflow activities
const ERROR_THRESHOLD_MS = 60_000; // Show error screen after 60 seconds

function getErrorMessage(
  returnType: ReturnTypeEnum,
  t: ReturnType<typeof useTranslation>["t"],
  hasCreationStatusError: boolean,
): string {
  const entityName =
    returnType === ReturnTypeEnum.CLAIM
      ? "shipping claim"
      : returnType === ReturnTypeEnum.WARRANTY
        ? "warranty claim"
        : returnType === ReturnTypeEnum.RECYCLE
          ? "recycling request"
          : "return";

  const baseMessage = `Your ${entityName} was still created. We will send you an email when we are back up so you can continue with your ${entityName} process.`;
  const autoUpdateSuffix =
    " This page will automatically update when we are back up.";
  const closing = " We are sorry for the inconvenience.";

  const fullMessage = hasCreationStatusError
    ? baseMessage + closing
    : baseMessage + autoUpdateSuffix + closing;

  return t(fullMessage);
}

export const CreationStatus = memo(function CreationStatus({
  showCreationStatus,
  creationStatus,
  hasCreationStatusError = false,
  returnType = ReturnTypeEnum.RETURN,
}: {
  showCreationStatus: boolean;
  creationStatus: UseCreationStatusResult["creationStatus"];
  hasCreationStatusError?: boolean;
  returnType?: ReturnTypeEnum;
}) {
  const { t } = useTranslation();

  const timeBetweenAnimations = 4000;

  // Determine error scenario
  const workflowStalled =
    showCreationStatus &&
    creationStatus?.workflowStartTime &&
    Date.now() - new Date(creationStatus.workflowStartTime).getTime() >
      ERROR_THRESHOLD_MS;

  const shouldShowError = hasCreationStatusError || workflowStalled;

  if (!showCreationStatus) {
    return null;
  }

  if (shouldShowError) {
    return (
      <Card fullscreen uiSelector="return-confirmation">
        <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
          <Icon arbiterIconSvg={FaceFrownIcon} color="ghost" size="large" />
          <Text
            color="text-primary"
            fontSize="xl"
            fontWeight="semibold"
            textAlign="center"
          >
            {t("Something is going wrong on our end")}
          </Text>
          <Text
            color="text-secondary"
            fontSize="md"
            style={{ maxWidth: "500px" }}
            textAlign="center"
          >
            {getErrorMessage(returnType, t, hasCreationStatusError)}
          </Text>
        </Flex>
      </Card>
    );
  }

  let percentage = 0;
  if (creationStatus?.activityStatuses) {
    const relevantActivities = creationStatus.activityStatuses || [];
    const completedActivities = relevantActivities.filter(
      (activity) => activity.status === Status.COMPLETED,
    ).length;
    const totalActivitiesStarted = relevantActivities.length;

    let timeProgress = 0;
    let elapsedMs = 0;
    if (creationStatus.workflowStartTime) {
      elapsedMs =
        Date.now() - new Date(creationStatus.workflowStartTime).getTime();
      timeProgress = Math.min(
        (elapsedMs / EXPECTED_WORKFLOW_DURATION_MS) * MAX_TIME_BASED_PROGRESS,
        MAX_TIME_BASED_PROGRESS,
      );
    }
    const estimatedTotal = Math.max(
      ESTIMATED_TOTAL_ACTIVITIES,
      totalActivitiesStarted + 3,
      completedActivities + 5,
    );

    const activityProgress = (completedActivities / estimatedTotal) * 100;
    const weightedProgress = timeProgress * 0.3 + activityProgress * 0.7;
    const activityBoost = completedActivities * ACTIVITY_BOOST;

    const baselineProgress = Math.min(elapsedMs > 0 ? 10 : 0, 10);

    const progress = Math.min(
      baselineProgress + weightedProgress + activityBoost,
      99,
    );

    if (creationStatus.isWorkflowRunning === false) {
      percentage = 100;
    } else {
      percentage = Math.round(Math.max(progress, percentage * 0.95));
    }
  }

  return (
    <Card fullscreen uiSelector="return-confirmation">
      <Flex align="center" dir="column" gap="lg" grow={1} justify="center">
        <TypeAnimation
          className={confirmationCss.typeAnimationText}
          cursor={false}
          omitDeletionAnimation
          sequence={[
            returnType === ReturnTypeEnum.CLAIM
              ? t("Creating shipping claim...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Creating warranty claim...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Creating recycling request...")
                  : t("Creating return..."),
            timeBetweenAnimations,
            returnType === ReturnTypeEnum.CLAIM
              ? t("Generating shipping claim label...")
              : returnType === ReturnTypeEnum.WARRANTY
                ? t("Generating warranty claim label...")
                : returnType === ReturnTypeEnum.RECYCLE
                  ? t("Generating recycling request label...")
                  : t("Generating return label..."),
            timeBetweenAnimations,
            t("Updating order..."),
            timeBetweenAnimations,
            t("Finishing up..."),
            timeBetweenAnimations,
            t("This is taking longer than expected..."),
          ]}
          speed={80}
          style={{ height: "20px" }}
          wrapper="span"
        />

        <Flex dir="column" gap="sm" style={{ maxWidth: "400px" }} w="full">
          <RedoProgressBar percentage={percentage} />
          <Text color="text-secondary" fontSize="xs" textAlign="center">
            {t("{{percentage}}% complete", {
              percentage: Math.round(percentage),
            })}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
});
